import type { CV } from '@cv-builder/core'

/**
 * Sample CV data for demo purposes
 * Contains enough content to generate a multi-page PDF
 */
export const sampleCV: Omit<CV, 'id' | 'createdAt' | 'updatedAt'> = {
  title: 'Sample CV - Software Engineer',
  templateId: 'classic',
  locale: 'en-US',
  personalInfo: {
    fullName: 'Alexandra Chen',
    jobTitle: 'Senior Full Stack Engineer',
    email: 'alexandra.chen@email.com',
    phone: '+1 (555) 123-4567',
    address: '742 Innovation Drive',
    city: 'San Francisco',
    country: 'USA',
    postalCode: '94102',
    website: 'https://alexandrachen.dev',
    linkedin: 'linkedin.com/in/alexandrachen',
    summary:
      'Passionate full-stack engineer with 8+ years of experience building scalable web applications and leading high-performing engineering teams. Expertise in React, Node.js, TypeScript, and cloud-native architectures. Proven track record of delivering complex projects on time while mentoring junior developers and driving engineering best practices. Strong advocate for clean code, test-driven development, and continuous improvement.',
    photoUrl: '',
    showPhoto: false,
  },
  experiences: [
    {
      id: 'exp-1',
      company: 'TechCorp Innovation Labs',
      position: 'Senior Full Stack Engineer',
      location: 'San Francisco, CA',
      startDate: '2021-03',
      endDate: null,
      isCurrent: true,
      sortOrder: 0,
      description: `• Led development of a real-time collaboration platform serving 500K+ daily active users, reducing page load times by 60% through code splitting and lazy loading strategies
• Architected and implemented a microservices migration from monolithic Rails app to Node.js/TypeScript services, improving deployment frequency from weekly to multiple times daily
• Mentored team of 5 junior developers through code reviews, pair programming, and weekly tech talks, resulting in 40% improvement in code quality metrics
• Designed and built a GraphQL API layer that unified 12 backend services, reducing frontend API calls by 70% and improving developer productivity
• Implemented comprehensive CI/CD pipelines with GitHub Actions, automated testing coverage from 45% to 92%, and established deployment best practices`,
    },
    {
      id: 'exp-2',
      company: 'StartupXYZ',
      position: 'Full Stack Developer',
      location: 'Austin, TX',
      startDate: '2018-06',
      endDate: '2021-02',
      isCurrent: false,
      sortOrder: 1,
      description: `• Built and maintained a B2B SaaS platform from ground up, growing from 0 to 10,000 paying customers in 2 years
• Developed a custom React component library with 50+ reusable components, reducing frontend development time by 35%
• Implemented real-time features using WebSockets and Redis pub/sub, enabling live updates for 100K+ concurrent users
• Optimized PostgreSQL queries and implemented caching strategies, reducing average API response time from 800ms to 120ms
• Collaborated with product team to define technical requirements and delivered features that increased user engagement by 45%`,
    },
    {
      id: 'exp-3',
      company: 'Digital Agency Pro',
      position: 'Frontend Developer',
      location: 'Remote',
      startDate: '2016-09',
      endDate: '2018-05',
      isCurrent: false,
      sortOrder: 2,
      description: `• Developed responsive web applications for 20+ clients across e-commerce, healthcare, and fintech industries
• Migrated legacy jQuery applications to modern React/Redux architecture, improving maintainability and performance
• Implemented accessibility (WCAG 2.1) improvements across client projects, ensuring compliance with legal requirements
• Created automated testing suites using Jest and Cypress, reducing regression bugs by 60%
• Worked directly with clients to gather requirements and provide technical consultations`,
    },
    {
      id: 'exp-4',
      company: 'WebDev Solutions',
      position: 'Junior Developer',
      location: 'Boston, MA',
      startDate: '2014-07',
      endDate: '2016-08',
      isCurrent: false,
      sortOrder: 3,
      description: `• Developed and maintained WordPress and custom PHP websites for small to medium businesses
• Built custom themes and plugins to meet specific client requirements
• Collaborated with design team to implement pixel-perfect responsive layouts
• Provided technical support and training to clients on content management systems`,
    },
  ],
  education: [
    {
      id: 'edu-1',
      institution: 'Massachusetts Institute of Technology',
      degree: 'Master of Science',
      fieldOfStudy: 'Computer Science',
      location: 'Cambridge, MA',
      startDate: '2012-09',
      endDate: '2014-06',
      isCurrent: false,
      grade: 'GPA: 3.9/4.0',
      sortOrder: 0,
      description:
        'Specialization in Distributed Systems and Machine Learning. Thesis: "Optimizing Real-time Data Processing in Distributed Environments".',
    },
    {
      id: 'edu-2',
      institution: 'University of California, Berkeley',
      degree: 'Bachelor of Science',
      fieldOfStudy: 'Computer Science',
      location: 'Berkeley, CA',
      startDate: '2008-09',
      endDate: '2012-05',
      isCurrent: false,
      grade: 'GPA: 3.8/4.0',
      sortOrder: 1,
      description:
        'Dean\'s List all semesters. President of Women in Computer Science club. Completed honors thesis on "Machine Learning Applications in Web Development".',
    },
  ],
  skills: [
    { id: 'skill-1', name: 'TypeScript', level: 'expert', category: 'Programming', sortOrder: 0 },
    { id: 'skill-2', name: 'React', level: 'expert', category: 'Frontend', sortOrder: 1 },
    { id: 'skill-3', name: 'Node.js', level: 'expert', category: 'Backend', sortOrder: 2 },
    { id: 'skill-4', name: 'GraphQL', level: 'advanced', category: 'API', sortOrder: 3 },
    { id: 'skill-5', name: 'PostgreSQL', level: 'advanced', category: 'Database', sortOrder: 4 },
    { id: 'skill-6', name: 'MongoDB', level: 'advanced', category: 'Database', sortOrder: 5 },
    { id: 'skill-7', name: 'AWS', level: 'advanced', category: 'Cloud', sortOrder: 6 },
    { id: 'skill-8', name: 'Docker', level: 'advanced', category: 'DevOps', sortOrder: 7 },
    { id: 'skill-9', name: 'Kubernetes', level: 'intermediate', category: 'DevOps', sortOrder: 8 },
    { id: 'skill-10', name: 'Python', level: 'advanced', category: 'Programming', sortOrder: 9 },
    { id: 'skill-11', name: 'Go', level: 'intermediate', category: 'Programming', sortOrder: 10 },
    { id: 'skill-12', name: 'Redis', level: 'advanced', category: 'Database', sortOrder: 11 },
    { id: 'skill-13', name: 'CI/CD', level: 'expert', category: 'DevOps', sortOrder: 12 },
    {
      id: 'skill-14',
      name: 'System Design',
      level: 'advanced',
      category: 'Architecture',
      sortOrder: 13,
    },
    {
      id: 'skill-15',
      name: 'Agile/Scrum',
      level: 'expert',
      category: 'Methodology',
      sortOrder: 14,
    },
  ],
  languages: [
    { id: 'lang-1', name: 'English', proficiency: 'native', sortOrder: 0 },
    { id: 'lang-2', name: 'Mandarin Chinese', proficiency: 'native', sortOrder: 1 },
    { id: 'lang-3', name: 'Spanish', proficiency: 'intermediate', sortOrder: 2 },
    { id: 'lang-4', name: 'French', proficiency: 'basic', sortOrder: 3 },
  ],
  certifications: [
    {
      id: 'cert-1',
      name: 'AWS Solutions Architect Professional',
      issuer: 'Amazon Web Services',
      issueDate: '2023-05',
      expiryDate: '2026-05',
      credentialId: 'AWS-SAP-12345',
      credentialUrl: 'https://aws.amazon.com/verification',
      sortOrder: 0,
    },
    {
      id: 'cert-2',
      name: 'Google Cloud Professional Cloud Architect',
      issuer: 'Google Cloud',
      issueDate: '2022-11',
      expiryDate: '2024-11',
      credentialId: 'GCP-PCA-67890',
      credentialUrl: '',
      sortOrder: 1,
    },
    {
      id: 'cert-3',
      name: 'Certified Kubernetes Administrator (CKA)',
      issuer: 'Cloud Native Computing Foundation',
      issueDate: '2022-03',
      expiryDate: '2025-03',
      credentialId: 'CKA-2022-ABC',
      credentialUrl: '',
      sortOrder: 2,
    },
    {
      id: 'cert-4',
      name: 'MongoDB Certified Developer',
      issuer: 'MongoDB, Inc.',
      issueDate: '2021-08',
      expiryDate: null,
      credentialId: 'MDB-DEV-XYZ',
      credentialUrl: '',
      sortOrder: 3,
    },
  ],
  customSections: [
    {
      id: 'custom-1',
      title: 'Open Source Contributions',
      sortOrder: 0,
      content: `**React Query** - Core Contributor
Contributed performance optimizations and bug fixes to the popular data-fetching library. Implemented the staleTime optimization that reduced unnecessary refetches by 40%.

**Fastify** - Plugin Maintainer
Maintain the official fastify-redis plugin with 50K+ weekly downloads. Implemented connection pooling and cluster support.

**TypeScript ESLint** - Contributor
Contributed new rules for detecting common TypeScript anti-patterns. Helped improve documentation for custom rule development.`,
    },
    {
      id: 'custom-2',
      title: 'Speaking & Publications',
      sortOrder: 1,
      content: `**ReactConf 2024** - Speaker
"Building Real-time Collaborative Applications at Scale" - Presented techniques for handling 100K+ concurrent users with React and WebSockets.

**NodeConf EU 2023** - Speaker
"From Monolith to Microservices: A Practical Migration Guide" - Shared lessons learned from migrating a 500K LOC codebase.

**O'Reilly Media** - Technical Author
Co-authored "Modern Full-Stack Development with TypeScript" covering React, Node.js, and cloud deployment strategies.`,
    },
    {
      id: 'custom-3',
      title: 'Awards & Recognition',
      sortOrder: 2,
      content: `**Tech Innovator of the Year** - TechCorp Innovation Labs, 2023
Recognized for leading the successful migration to microservices architecture and mentoring initiatives.

**Best Technical Talk** - ReactConf 2024
Voted best technical presentation by conference attendees.`,
    },
  ],
}

export const danishSampleCV: Omit<CV, 'id' | 'createdAt' | 'updatedAt'> = {
  title: 'Dansk CV - Produktdesigner',
  templateId: 'sidebar-left',
  locale: 'da-DK',
  personalInfo: {
    fullName: 'Sofie Madsen',
    jobTitle: 'Senior Produktdesigner',
    email: 'sofie.madsen@example.dk',
    phone: '+45 26 34 78 91',
    address: 'Vesterbrogade 42',
    city: 'København',
    country: 'Danmark',
    postalCode: '1620',
    website: 'https://sofiemadsen.dk',
    linkedin: 'linkedin.com/in/sofiemadsen',
    summary:
      'Produktdesigner med 9 års erfaring i at omsætte komplekse arbejdsgange til klare digitale produkter. Har arbejdet tæt med udvikling, forretning og brugere i SaaS- og offentlige løsninger. Særlig styrke i research, informationsarkitektur, designsystemer og tilgængelighed.',
    photoUrl: '',
    showPhoto: false,
  },
  experiences: [
    {
      id: 'dk-exp-1',
      company: 'Nordic Workflow ApS',
      position: 'Senior Produktdesigner',
      location: 'København',
      startDate: '2021-02',
      endDate: null,
      isCurrent: true,
      sortOrder: 0,
      description: `• Leder designarbejdet for en B2B-platform med 35.000 aktive brugere på tværs af Norden
• Gennemførte researchforløb med kundeservice, drift og slutbrugere, som reducerede onboarding-tiden med 28%
• Opbyggede et designsystem i tæt samarbejde med frontend-teamet og gjorde komponenter klar til WCAG 2.2 AA
• Faciliterer workshops med produktchefer og udviklere for at prioritere løsninger ud fra brugerbehov og teknisk risiko`,
    },
    {
      id: 'dk-exp-2',
      company: 'BorgerLab',
      position: 'UX Designer',
      location: 'Aarhus',
      startDate: '2017-08',
      endDate: '2021-01',
      isCurrent: false,
      sortOrder: 1,
      description: `• Designede selvbetjeningsflows for kommunale services med fokus på forståeligt sprog og lav fejlrate
• Planlagde og afholdt brugertests med borgere, sagsbehandlere og supportmedarbejdere
• Dokumenterede service blueprints og gjorde afhængigheder synlige for både ledelse og udviklingsteam
• Bidrog til en fælles designmanual, der blev brugt på 12 digitale løsninger`,
    },
    {
      id: 'dk-exp-3',
      company: 'Studio Nord',
      position: 'Digital Designer',
      location: 'Odense',
      startDate: '2014-09',
      endDate: '2017-07',
      isCurrent: false,
      sortOrder: 2,
      description: `• Udarbejdede responsive websites og kampagnesider for kunder inden for kultur, uddannelse og detail
• Arbejdede med wireframes, prototyper og visuel identitet fra første koncept til lancering
• Samarbejdede direkte med kunder om krav, indhold og lancering`,
    },
  ],
  education: [
    {
      id: 'dk-edu-1',
      institution: 'IT-Universitetet i København',
      degree: 'Kandidat',
      fieldOfStudy: 'Digital Design og Interaktive Teknologier',
      location: 'København',
      startDate: '2012-09',
      endDate: '2014-06',
      isCurrent: false,
      grade: '10,8 i gennemsnit',
      sortOrder: 0,
      description:
        'Speciale om digitale arbejdsgange i komplekse organisationer med fokus på prototyping, brugerinddragelse og organisatorisk forankring.',
    },
    {
      id: 'dk-edu-2',
      institution: 'Danmarks Medie- og Journalisthøjskole',
      degree: 'Professionsbachelor',
      fieldOfStudy: 'Interaktivt Design',
      location: 'København',
      startDate: '2009-08',
      endDate: '2012-06',
      isCurrent: false,
      grade: '',
      sortOrder: 1,
      description: 'Fokus på visuel kommunikation, webdesign, brugerrejser og digitale kampagner.',
    },
  ],
  skills: [
    {
      id: 'dk-skill-1',
      name: 'Produktstrategi',
      level: 'expert',
      category: 'Produkt',
      sortOrder: 0,
    },
    { id: 'dk-skill-2', name: 'Brugerresearch', level: 'expert', category: 'UX', sortOrder: 1 },
    { id: 'dk-skill-3', name: 'Figma', level: 'expert', category: 'Designværktøjer', sortOrder: 2 },
    {
      id: 'dk-skill-4',
      name: 'Designsystemer',
      level: 'advanced',
      category: 'Design',
      sortOrder: 3,
    },
    {
      id: 'dk-skill-5',
      name: 'Tilgængelighed',
      level: 'advanced',
      category: 'Kvalitet',
      sortOrder: 4,
    },
    { id: 'dk-skill-6', name: 'Prototyping', level: 'advanced', category: 'UX', sortOrder: 5 },
    { id: 'dk-skill-7', name: 'Service design', level: 'advanced', category: 'UX', sortOrder: 6 },
    {
      id: 'dk-skill-8',
      name: 'HTML/CSS',
      level: 'intermediate',
      category: 'Frontend',
      sortOrder: 7,
    },
  ],
  languages: [
    { id: 'dk-lang-1', name: 'Dansk', proficiency: 'native', sortOrder: 0 },
    { id: 'dk-lang-2', name: 'Engelsk', proficiency: 'advanced', sortOrder: 1 },
    { id: 'dk-lang-3', name: 'Svensk', proficiency: 'intermediate', sortOrder: 2 },
  ],
  certifications: [
    {
      id: 'dk-cert-1',
      name: 'Certified Professional in Accessibility Core Competencies',
      issuer: 'IAAP',
      issueDate: '2023-04',
      expiryDate: '2026-04',
      credentialId: 'IAAP-CPACC-2048',
      credentialUrl: '',
      sortOrder: 0,
    },
    {
      id: 'dk-cert-2',
      name: 'Design Sprint Facilitator',
      issuer: 'AJ&Smart',
      issueDate: '2020-11',
      expiryDate: null,
      credentialId: '',
      credentialUrl: '',
      sortOrder: 1,
    },
  ],
  customSections: [
    {
      id: 'dk-custom-1',
      title: 'Udvalgte resultater',
      sortOrder: 0,
      content: `**Onboarding i enterprise-produkt**
Reducerede tiden fra første login til færdig opsætning fra 42 minutter til 30 minutter gennem research, ny informationsarkitektur og bedre tomme states.

**Designsystem til tværgående teams**
Etablerede fælles komponentbibliotek, dokumentation og review-flow, som gjorde det lettere at bygge ensartede skærmbilleder på tværs af 4 produktteams.`,
    },
    {
      id: 'dk-custom-2',
      title: 'Frivilligt arbejde',
      sortOrder: 1,
      content:
        'Mentor for yngre designere gennem Ladies That UX Copenhagen. Hjælper med portfoliofeedback, jobsamtaler og sparring om samarbejde med udviklingsteams.',
    },
  ],
}

/**
 * Creates a complete sample CV with generated IDs and timestamps
 */
export function createSampleCV(): CV {
  const now = new Date().toISOString()
  return {
    ...sampleCV,
    id: 'sample-cv-001',
    createdAt: now,
    updatedAt: now,
  }
}

export function createDanishSampleCV(): CV {
  const now = new Date().toISOString()
  return {
    ...danishSampleCV,
    id: 'danish-sample-cv-001',
    createdAt: now,
    updatedAt: now,
  }
}

/**
 * Creates an empty CV with all required fields but no content
 */
export function createEmptyCV(): CV {
  const now = new Date().toISOString()
  return {
    id: 'empty-cv-001',
    title: 'Nyt CV',
    templateId: 'classic',
    locale: 'da-DK',
    personalInfo: {
      fullName: '',
      jobTitle: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      country: '',
      postalCode: '',
      website: '',
      linkedin: '',
      summary: '',
      photoUrl: '',
      showPhoto: false,
    },
    experiences: [],
    education: [],
    skills: [],
    languages: [],
    certifications: [],
    customSections: [],
    createdAt: now,
    updatedAt: now,
  }
}
